import { createSelector, createSlice } from '@reduxjs/toolkit';
import { CONSTANTS } from '../../../components/Helpers/Configuration';
import set from 'lodash/set';
import get from 'lodash/get';
const { applicationFormFilterMode, sortDirections } = CONSTANTS;

const adminBaseSlice = createSlice({
    name: 'adminBase',
    initialState: {
        allTemplates: null,
        currentTemplate: null,
        allOnlineTemplates: null,
        allDeliveredTemplates: null,
        filterMode: [applicationFormFilterMode.editpublished],
        hedmModelAttributeCache: {},
        homepageSearch: '',
        pageHeader: '',
        previewConfig: {
            priorLocation: null,
            template: null
        },
        selectedSetting: CONSTANTS.globalSettings.generalSettings.emailValidation,
        templatedFields: [],
        tenant: {
            supportedGraphqlModels: [],
            themeAppId: ''
        },
        gridView: true,
        recaptchaClientTokenError: false,
        recaptchaServerTokenError: false,
        offset: 0,
        max: 10,
        sort: 'updatedAt',
        pagination: 10,
        direction: sortDirections.descending,
        searchString: '',
        totalCount: 0,
    },
    reducers: {
        setSearchString: (state, action) => {
            state.searchString = action.payload;
            return state;
        },
        setSort: (state, action) => {
            state.sort = action.payload;
            return state;
        },
        setPagination: (state, action) => {
            state.pagination = action.payload;
            return state;
        },
        setDirection: (state, action) => {
            state.direction = action.payload;
            return state;
        },
        setOffset: (state, action) => {
            state.offset = action.payload;
            return state;
        },
        setMax: (state, action) => {
            state.max = action.payload;
            return state;
        },
        clearHomepageSearch: (state) => {
            state.homepageSearch = '';
        },
        setAllTemplates: (state, action) => {
            state.allTemplates = action.payload;
            return state;
        },
        setAllOnlineTemplates: (state, action) => {
            state.allOnlineTemplates = action.payload.records;
            state.totalCount = action.payload.totalCount;
            return state;
        },
        setAllDeliveredTemplates: (state, action) => {
            state.allDeliveredTemplates = action.payload;
            return state;
        },
        setCurrentTemplate: (state, action) => {
            state.currentTemplate = action.payload;
            return state;
        },
        setFilterMode: (state, action) => {
            state.filterMode = action.payload;
            return state;
        },
        setHedmModelAttributes: (state, action) => {
            state.hedmModelAttributeCache[action.payload.name] = action.payload.attributes;
            return state;
        },
        setHomepageSearch: (state, action) => {
            state.homepageSearch = action.payload;
        },
        setPageHeader: (state, action) => {
            state.pageHeader = action.payload;
        },
        setPreviewConfig: (state, action) => {
            state.previewConfig = action.payload;
        },
        returnFromPreview: (state) => {
            const currentTemplate = state.currentTemplate;
            state.currentTemplate = currentTemplate;
        },
        setTenant: (state, action) => {
            state.tenant = action.payload;
        },
        setTenantHasApiKey: (state, action) => {
            state.tenant.noApiKey = !action.payload;
        },
        updateGlobalSettingsDataProperty: (state, action) => {
            const { properties } = action.payload;

            Object.keys(properties).map(property => {
                set(state, property, properties[property]);
            });

            return state;
        },
        setSelectedSetting: (state, action) => {
            state.selectedSetting = action.payload;

            return state;
        },
        setTemplatedFields: (state, action) => {
            state.templatedFields = action.payload;

            return state;
        },
        setLocalizedSettingValue: (state, action) => {
            const { settingPath, value } = action.payload;
            get(state.tenant, settingPath).localizedValue = value;

            return state;
        },
        setGridView: (state, action) => {
            state.gridView = action.payload;
        },
        setRecaptchaClientTokenError: (state, action) => {
            state.recaptchaClientTokenError = action.payload;
        },
        setRecaptchaServerTokenError: (state, action) => {
            state.recaptchaServerTokenError = action.payload;
        }
    },
});
export const {
    setSearchString,
    setSort,
    setPagination,
    setDirection,
    setFilter,
    setOffset,
    setMax,
    clearHomepageSearch,
    setAllTemplates,
    setAllOnlineTemplates,
    setAllDeliveredTemplates,
    setCurrentTemplate,
    setFilterMode,
    setHedmModelAttributes,
    setPageHeader,
    setPreviewConfig,
    setHomepageSearch,
    setLocalizedSettingValue,
    setTenant,
    setTenantHasApiKey,
    setSelectedSetting,
    updateGlobalSettingsDataProperty,
    returnFromPreview,
    setTemplatedFields,
    setGridView,
    setRecaptchaClientTokenError,
    setRecaptchaServerTokenError
} = adminBaseSlice.actions;

export const selectCurrentTemplate = (state) => state.adminBase?.currentTemplate;
export const selectAllTemplates = (state) => state.adminBase.allTemplates;
export const selectAllOnlineTemplates = (state) => state.adminBase.allOnlineTemplates;
export const selectAllDeliveredTemplates = (state) => state.adminBase.allDeliveredTemplates;
export const selectFilterMode = (state) => state.adminBase.filterMode;
export const selectHomepageSearch = state => state.adminBase.homepageSearch;
export const selectPageHeader = (state) => state.adminBase.pageHeader;
export const selectPreviewConfig = (state) => state.adminBase.previewConfig;
export const selectGraphQlModels = (state) => state.adminBase.tenant.supportedGraphqlModels;
export const selectTenant = (state) => state.adminBase.tenant;
export const selectTenantHasApiKey = (state) => state.adminBase.tenant && !state.adminBase.tenant.noApiKey;
export const selectSelectedSetting = (state) => state.adminBase.selectedSetting;
export const selectTemplatedFields = (state) => state.adminBase.templatedFields;
export const selectGridView = (state) => state.adminBase.gridView;
export const selectRecaptchaClientTokenError = (state) => state.adminBase.recaptchaClientTokenError;
export const selectRecaptchaServerTokenError = (state) => state.adminBase.recaptchaServerTokenError;
export const selectSearchString = (state) => state.adminBase.searchString;
export const selectSort = (state) => state.adminBase.sort;
export const selectPagination = state => state.adminBase.pagination;
export const selectDirection = (state) => state.adminBase.direction;
export const selectTotalCount = (state) => state.adminBase.totalCount;
export const selectStatusFilter = (state) => state.adminBase.statusFilter;
export const selectOffset = (state) => state.adminBase.offset;
export const selectMax = (state) => state.adminBase.max;

export const createTemplateIdSelector = (templateType, status) =>
    createSelector(
        state => state.adminBase.allTemplates,
        allTemplates => allTemplates?.filter(template =>
            template.templateType === templateType &&
            template.status === status
        ).map(obj => obj.id) || []
    );

export default adminBaseSlice.reducer;
